/**
 * Responsibility: decide which biomes are present at a world coordinate, and how strongly.
 *
 * This is the algorithm the whole world hangs off. Elevation, colour, and eventually
 * structure density are all weighted sums over what this returns, so its two properties
 * matter more than anything else here:
 *
 *   1. **Weights always sum to 1.** Downstream code treats them as proportions and never
 *      renormalises, so a provider that drifts off 1 would quietly brighten or darken
 *      colours and raise or lower terrain.
 *   2. **Weights vary continuously.** Any step here becomes a visible seam in the terrain.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 *
 * ## How a coordinate is judged
 *
 * Every biome has a low-frequency suitability field saying how much it wants to be here.
 * The strongest wins. Others contribute in proportion to how close behind they are, and
 * anything more than `BLEND_WIDTH` behind contributes nothing.
 *
 * That last part is the whole design. The obvious approach — normalise the top few
 * suitabilities directly — cannot produce a pure region: a leader on 0.8 against a
 * runner-up on 0.7 still comes out 53/47, so everywhere is a blend of everything and no
 * biome ever looks like itself. Measuring distance *behind the leader* gives the runner-up a
 * definite point where it reaches zero.
 *
 * ## Why transitions need no special-case code
 *
 * The suitability fields are smooth, so the gap between leader and runner-up closes
 * gradually as you fly toward a boundary. Elevation being a weighted sum then means
 * mountains genuinely shrink into flatland over a few thousand units. There is no
 * transition code anywhere, and there is no lookahead system either — sampling ahead of the
 * player is this same function at a different coordinate.
 */

import { createFbm, toUnitRange } from './noise.js';
import { BIOMES } from './biomes/index.js';
import { BIOME } from '../config.js';

/**
 * Middle of the 0..1 range a remapped suitability field occupies.
 *
 * Not a tuning value: it is where `toUnitRange` puts zero, so it is the point a field's deviations are
 * measured from. Named because the mega composition centres on it three times and a bare 0.5 there would
 * read as arbitrary.
 */
const CENTRE = 0.5;

/**
 * Create a biome field sampler.
 *
 * @param {object} options
 * @param {number} options.seed - Base world seed.
 * @param {Array<object>} [options.biomes] - Biome descriptors. Defaults to the registry.
 * @param {object} [options.tuning] - Overrides for the `BIOME` config block. Tests use round
 *   numbers so failures point at the algorithm rather than at the current tuning.
 * @returns {{sample: Function, sampleSuitability: Function, getBiomeIds: Function}}
 */
export function createBiomeField({ seed, biomes = BIOMES, tuning: overrides = BIOME }) {
  /*
    Merged over the real config rather than used as-is, so a test can override the two or three values it
    cares about without having to restate every key the algorithm reads. Before this, adding a config key
    broke every test that passed a partial tuning object.
  */
  const tuning = overrides === BIOME ? BIOME : { ...BIOME, ...overrides };

  if (!Array.isArray(biomes) || biomes.length === 0) {
    throw new Error('createBiomeField: at least one biome is required');
  }
  if (!(tuning.BLEND_WIDTH > 0)) {
    // Zero width would make every boundary a hard seam with no lead-in at all.
    throw new Error('createBiomeField: BLEND_WIDTH must be positive');
  }
  if (!(tuning.WEIGHT_SHARPNESS > 0)) {
    throw new Error('createBiomeField: WEIGHT_SHARPNESS must be positive');
  }
  if (!Number.isInteger(tuning.MAX_BLEND_COUNT) || tuning.MAX_BLEND_COUNT < 1) {
    throw new Error('createBiomeField: MAX_BLEND_COUNT must be an integer of at least 1');
  }

  /*
    One suitability source per biome, built once. Each closes over its own noise field, which
    is expensive to construct, so building them per sample would be ruinous.

    A biome declaring `constantSuitability` gets no field at all — that is what makes it the
    default rather than another competing region. See `biomes/baseline.js`.
  */
  const sources = biomes.map((biome) => {
    const bias = Number.isFinite(biome.suitabilityBias) ? biome.suitabilityBias : 0;

    if (Number.isFinite(biome.constantSuitability)) {
      /*
        **The default biome gets no mega term at all**, and that is deliberate rather than an omission. It is
        defined as a fixed level that every other biome is measured against, so it has no notion of scale to
        enlarge, and it must not be renormalised either — it is the reference the renormalisation preserves
        the spread *relative to*.

        What happens instead is exactly right. In a mega area the other biomes keep their full spread but
        distribute it over much larger areas, so this level is beaten over larger areas and undercut over
        larger areas — which produces bigger tracts of open ground as well as bigger cities, with nothing here
        saying so.
      */
      const level = biome.constantSuitability;
      return { id: biome.id, bias, raw: () => level, mega: null };
    }

    /*
      **How big this biome's regions are, which is a property of the biome and not of the world.**

      Almost every biome leaves this alone and takes the shared wavelength, because a region being about a
      minute across is what makes the world feel like it has places in it. The fairground is the exception
      that this override exists for: what it builds is a *single site*, and a region sized like a city's held
      several complete fairgrounds side by side, which reads as a mistake rather than as a big fairground.
      Shrinking its field is the only thing that makes one park fill one region.

      Note this changes the *scale* of that biome's regions without changing its share of the world. Uniformly
      stretching or squeezing the domain of an fBm field leaves its distribution of values alone, so the field
      still beats its rivals exactly as often — the same total area, cut into more and smaller patches. A
      biome's share is moved with `suitabilityBias`, never with this.
    */
    const wavelength = Number.isFinite(biome.fieldWavelength)
      ? biome.fieldWavelength
      : tuning.FIELD_WAVELENGTH;

    /*
      A distinct label per biome, so every field is statistically independent. Without it,
      mountains and cities would want the same places and the world would have one kind of
      region wearing different clothes.
    */
    const field = createFbm(seed, `biome:${biome.id}`, {
      octaves: tuning.FIELD_OCTAVES,
      baseFrequency: 1 / wavelength,
    });

    /*
      The same biome at the mega scale.

      **A different label, not the same field stretched.** Reusing `biome:${id}` at a longer wavelength would
      make the coarse field a zoomed-out view of the same shapes, so a mega region would sit in the same
      place as the normal region it replaces and the two would be correlated in a way that reads as the
      world having one layout at two sizes.

      A biome may decline the mega scale altogether. The fairground does, and for the same reason it sets its own
      wavelength: what it builds is one site, so there is no such thing as a larger version of it. Measured
      before the opt-out, a seed that happened to put a mega patch over a fairground region gave regions 14,000
      units across as flown — flat empty plain, three times the size of an ordinary biome's, with a single
      fairground somewhere in it.

      Opting out is safe against the balance argument below. The renormalisation exists to hold each biome's
      *spread* constant at any mega level, and a biome with no coarse term already has its spread unchanged, so
      it keeps competing on exactly the terms it does everywhere else. What it loses is only the change of
      scale: inside a mega patch its rivals' regions grow and its own stay small, so it survives as small
      patches poking through them, which is what a fairground should be.
    */
    const scalesWithMega = biome.scalesWithMega !== false;

    const coarseField = scalesWithMega
      ? createFbm(seed, `biome:mega:${biome.id}`, {
          octaves: tuning.MEGA_FIELD_OCTAVES,

          /*
            Scaled off this biome's own wavelength, not the shared one, so a biome that overrides its region size
            keeps that size in a mega area too.
          */
          baseFrequency: 1 / (wavelength * tuning.MEGA_SCALE),
        })
      : null;

    return {
      id: biome.id,

      /*
        **Held separately from the field rather than folded into it**, because the mega composition
        renormalises the field and the bias must not be renormalised with it.

        That was a real bug: dividing the bias by the spread factor diluted it more than threefold inside a
        mega area, so every biome drifted toward the mean there and the world had two different balances
        depending on the scale. Measured before the fix — river 7.2% of normal ground against 17.5% of mega,
        city 6.2% against 2.4%. The bias is the project's only dial for a biome's share, so it has to mean the
        same thing everywhere.
      */
      bias,

      // Remapped into 0..1 so a constant suitability is comparable against a noise field.
      raw: (x, z) => toUnitRange(field(x, z)),

      /*
        Centred on zero, so this is a *deviation* to add rather than a level to average with. See
        `suitabilityOf` for why that distinction decided the whole design.

        Null for a biome that declines the mega scale, which `suitabilityOf` reads as "use the normal field
        everywhere" — the same path the default biome takes.
      */
      mega: coarseField === null ? null : (x, z) => toUnitRange(coarseField(x, z)) - CENTRE,
    };
  });

  /**
   * The field that decides where the world runs at the larger scale.
   *
   * One field for the whole world rather than one per biome: "is this a place where regions are huge" is a
   * property of the place, not of any biome, and a per-biome version would let one biome be mega while its
   * neighbour was not — which would show up as a big region with a ragged edge instead of a big region.
   */
  const megaField = createFbm(seed, 'biome:mega-scale', {
    octaves: tuning.MEGA_OCTAVES,
    baseFrequency: 1 / tuning.MEGA_WAVELENGTH,
  });

  /**
   * How much the coarse fields are trusted at a coordinate.
   *
   * Zero over most of the world, one inside a mega patch, ramping between. The ramp is what keeps the
   * changeover invisible: a hard switch would put a seam along the threshold contour, and unlike a biome
   * boundary that seam would cut across whatever region happened to be there.
   *
   * @returns {number} 0 to 1.
   */
  function megaAt(x, z) {
    const level = toUnitRange(megaField(x, z));
    const t = (level - tuning.MEGA_THRESHOLD) / tuning.MEGA_RAMP;
    return t <= 0 ? 0 : t >= 1 ? 1 : t;
  }

  /**
   * A biome's suitability at a coordinate, including its mega-scale term.
   *
   * ## Two wrong compositions, and why the third is right
   *
   * **Averaging the two fields produces mush.** Two independent fields averaged have *less* variance than
   * either — at an even mix the spread shrinks by √2 — and the only reason pure regions exist is that
   * suitabilities are spread wide compared to `BLEND_WIDTH`. Measured on the first attempt: five biomes
   * landed inside a range of 0.16 against a blend width of 0.2, so every point in a mega area was a
   * three-way blend and rank swaps at the `MAX_BLEND_COUNT` boundary happened constantly. A continuity test
   * caught it.
   *
   * **Simply adding the coarse term goes wrong the other way.** It widens the spread by `MEGA_BOOST`, and
   * the default biome's suitability is a *fixed level*, not a distribution — so a wider spread means the
   * others clear that level far more often and open ground all but disappears from mega areas. The balance
   * of the world would depend on the scale, which it must not.
   *
   * **So the sum is renormalised to hold the spread constant.** Dividing the centred sum by
   * `sqrt(1 + (boost·mega)²)` — the standard deviation of the sum of two independent terms with those
   * amplitudes — leaves the total spread exactly where it was at any mega level, while shifting *which
   * wavelength dominates* it. At full mega with a boost of 3 the coarse term carries three times the weight
   * of the normal one, so the regions follow the coarse field and the normal field survives as fine detail
   * on their edges.
   *
   * The default biome is deliberately left out of the renormalisation as well as out of the boost: its level
   * is the fixed thing everything else is measured against, so scaling it would move the very reference the
   * spread is being preserved relative to.
   *
   * ## The branch is an optimisation that matters
   *
   * This runs for every terrain vertex and every structure query in the world. Sampling the coarse field
   * everywhere would add thirteen fBm evaluations to the hottest path in the project. Over most of the world
   * `mega` is exactly 0 and the second field is never touched.
   */
  function suitabilityOf(source, x, z, mega) {
    const raw = source.raw(x, z);
    if (mega <= 0 || source.mega === null) return raw + source.bias;

    const amplitude = tuning.MEGA_BOOST * mega;
    const spread = Math.sqrt(1 + amplitude * amplitude);

    // Bias added after the renormalisation, never inside it — see the note on `bias` above.
    return CENTRE + (raw - CENTRE + amplitude * source.mega(x, z)) / spread + source.bias;
  }

  /*
    Reused between samples. This runs for every terrain vertex and every structure query in the world, so
    neither allocation nor a comparator call is affordable here.

    Suitabilities in a typed array with the ids in a fixed parallel list, rather than an array of objects
    that gets sorted. **Measured, ranking and normalising was 1.28 ms of the 2.70 ms this function cost per
    tile — nearly half, and none of it noise.** Sorting thirteen objects means a comparator call per
    comparison and it reorders the array, which is also why the previous version had to rewrite every id on
    every sample.
  */
  const ids = sources.map((source) => source.id);
  const suitabilities = new Float64Array(sources.length);
  const taken = new Uint8Array(sources.length);
  const ranked = new Int32Array(Math.min(tuning.MAX_BLEND_COUNT, sources.length));
  const shaped = new Float64Array(ranked.length);

  return {
    /**
     * Normalised biome weights at a coordinate.
     *
     * @param {number} x
     * @param {number} z
     * @returns {Array<{id: string, weight: number}>} Weights sum to 1. Only biomes with a
     *   non-zero weight are included, so callers can iterate without filtering.
     */
    sample(x, z) {
      let leader = Number.NEGATIVE_INFINITY;

      // One decision for the whole coordinate, shared by every biome — see `megaAt`.
      const mega = megaAt(x, z);

      for (let i = 0; i < sources.length; i += 1) {
        const suitability = suitabilityOf(sources[i], x, z, mega);
        suitabilities[i] = suitability;
        taken[i] = 0;

        if (suitability > leader) leader = suitability;
      }

      /*
        The strongest few, picked rather than sorted. Only the top MAX_BLEND_COUNT are considered, which is a
        cost cap rather than a look decision: anything beyond BLEND_WIDTH behind the leader already weighs
        nothing. Capping stays safe because a biome can only be displaced from the top few at the moment it
        ties with whatever replaces it, and tied biomes carry identical weights, so the result never jumps.

        **Selection has to be stable to match what a sort produced**, or ties would resolve differently and
        every world would shift. Scanning in registry order with a strict `>` keeps the lowest-indexed of any
        tied group, which is exactly what a stable sort does.
      */
      const considered = ranked.length;

      for (let slot = 0; slot < considered; slot += 1) {
        let best = -1;
        let bestSuitability = Number.NEGATIVE_INFINITY;

        for (let i = 0; i < sources.length; i += 1) {
          if (taken[i] === 1) continue;
          if (suitabilities[i] > bestSuitability) {
            bestSuitability = suitabilities[i];
            best = i;
          }
        }

        ranked[slot] = best;
        taken[best] = 1;
      }

      let total = 0;

      for (let slot = 0; slot < considered; slot += 1) {
        // Never negative: the leader is the maximum, so it is zero behind itself.
        const behind = leader - suitabilities[ranked[slot]];
        const closeness = 1 - behind / tuning.BLEND_WIDTH;

        // Held rather than recomputed below: `Math.pow` is the most expensive call left in this function.
        shaped[slot] = closeness <= 0 ? 0 : Math.pow(closeness, tuning.WEIGHT_SHARPNESS);
        total += shaped[slot];
      }

      const weights = [];
      for (let slot = 0; slot < considered; slot += 1) {
        if (shaped[slot] <= 0) continue;
        weights.push({ id: ids[ranked[slot]], weight: shaped[slot] / total });
      }

      /*
        The leader is always within zero of itself, so its closeness is 1 and the total can
        never be zero. If that ever changes, failing loudly beats silently returning weights
        that do not sum to 1 and letting the terrain quietly sink.
      */
      if (weights.length === 0) {
        throw new Error(`biomeField: no biome had any weight at (${x}, ${z})`);
      }

      return weights;
    },

    /**
     * Raw suitability per biome, before ranking or normalising.
     *
     * For the debug overlay and for tests that need to reason about why a coordinate came
     * out the way it did. Not used by generation.
     *
     * @param {number} x
     * @param {number} z
     * @returns {Array<{id: string, suitability: number}>} In registry order.
     */
    sampleSuitability(x, z) {
      /*
        At the same scale `sample` would use. **Not optional**: the tile builder decides which biomes to ask
        for structures from these numbers, so a version that ignored the mega blend would skip a biome that
        is about to lead a huge region.
      */
      const mega = megaAt(x, z);

      return sources.map((source) => ({
        id: source.id,
        suitability: suitabilityOf(source, x, z, mega),
      }));
    },

    /**
     * How much larger than normal the regions are at a coordinate.
     *
     * For the debug overlay and for measuring how much of the world runs at the larger scale, which is the
     * only way `MEGA_THRESHOLD` can be set honestly.
     *
     * @returns {number} 1 at the normal scale, up to `MEGA_SCALE` inside a mega patch.
     */
    sampleScale(x, z) {
      return 1 + megaAt(x, z) * (tuning.MEGA_SCALE - 1);
    },

    /** Ids of every biome this field can produce, in registry order. */
    getBiomeIds() {
      return sources.map((source) => source.id);
    },
  };
}
